import {
  MapPin,
  BedDouble,
  Maximize2,
  Banknote,
  Trash2,
  ExternalLink,
} from "lucide-react";
import type { Listing } from "../types";

interface ListingGridProps {
  listings: Listing[];
  onSelect: (listing: Listing) => void;
  onDelete: (id: string) => void;
}

export function ListingGrid({
  listings,
  onSelect,
  onDelete,
}: ListingGridProps) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
      {listings.map((listing, index) => (
        <ListingCard
          key={listing.id}
          listing={listing}
          index={index}
          onSelect={onSelect}
          onDelete={onDelete}
        />
      ))}
    </div>
  );
}

function ListingCard({
  listing,
  index,
  onSelect,
  onDelete,
}: {
  listing: Listing;
  index: number;
  onSelect: (listing: Listing) => void;
  onDelete: (id: string) => void;
}) {
  const mainImage = listing.images[0];
  const imageUrl = mainImage?.localFilename
    ? `/api/images/${encodeURI(mainImage.localFilename)}`
    : null;

  return (
    <article
      className="group relative rounded-2xl overflow-hidden bg-card border border-border/40 hover:border-primary/30 transition-all duration-300 cursor-pointer animate-in fade-in slide-in-from-bottom-4"
      style={{
        animationDelay: `${index * 60}ms`,
        animationFillMode: "backwards",
      }}
      onClick={() => onSelect(listing)}
    >
      {/* Image */}
      <div className="relative aspect-[4/3] overflow-hidden bg-muted">
        {imageUrl ? (
          <img
            src={imageUrl}
            alt={listing.streetAddress ?? "Bostad"}
            className="w-full h-full object-cover group-hover:scale-105"
            style={{ transition: "scale 120ms ease" }}
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-muted to-secondary">
            <MapPin className="w-10 h-10 text-muted-foreground/30" />
          </div>
        )}

        {/* Overlay gradient */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

        {/* Housing form badge */}
        {listing.housingForm && (
          <span className="absolute top-3 left-3 px-2.5 py-1 rounded-md bg-black/50 backdrop-blur-md text-[11px] font-medium text-white/90 tracking-wide uppercase">
            {listing.housingForm}
          </span>
        )}

        {/* Image count badge */}
        {listing.images.length > 1 && (
          <span className="absolute top-3 right-3 px-2 py-1 rounded-md bg-black/50 backdrop-blur-md text-[11px] text-white/80">
            {listing.images.length} bilder
          </span>
        )}

        {/* Actions on hover */}
        <div className="absolute bottom-3 right-3 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 translate-y-1 group-hover:translate-y-0">
          {listing.hemnetUrl && (
            <a
              href={listing.hemnetUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="w-8 h-8 rounded-lg bg-white/90 hover:bg-white flex items-center justify-center text-gray-700 transition-all shadow-lg"
              title="Öppna på Hemnet"
            >
              <ExternalLink className="w-3.5 h-3.5" />
            </a>
          )}
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDelete(listing.id);
            }}
            className="w-8 h-8 rounded-lg bg-white/90 hover:bg-red-50 flex items-center justify-center text-gray-700 hover:text-red-600 transition-all shadow-lg"
            title="Ta bort"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="p-4">
        {/* Price */}
        {listing.askingPriceFormatted && (
          <div className="text-primary font-semibold text-lg tracking-tight leading-tight mb-1">
            {listing.askingPriceFormatted}
          </div>
        )}

        {/* Address */}
        <h3 className="font-display text-base font-medium text-foreground leading-snug mb-2 line-clamp-1">
          {listing.streetAddress ?? "Okänd adress"}
        </h3>

        {/* Location */}
        {listing.locationName && (
          <div className="flex items-center gap-1.5 text-muted-foreground text-xs mb-3">
            <MapPin className="w-3 h-3" />
            <span className="truncate">
              {listing.locationFullName ?? listing.locationName}
            </span>
          </div>
        )}

        {/* Meta row */}
        <div className="flex items-center gap-3 pt-3 border-t border-border/40">
          {listing.numberOfRooms && (
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <BedDouble className="w-3.5 h-3.5" />
              <span>{listing.numberOfRooms} rum</span>
            </div>
          )}
          {listing.livingArea && (
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <Maximize2 className="w-3.5 h-3.5" />
              <span>{listing.livingArea}</span>
            </div>
          )}
          {listing.fee && (
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground ml-auto">
              <Banknote className="w-3.5 h-3.5" />
              <span>{listing.fee}/mån</span>
            </div>
          )}
        </div>
      </div>
    </article>
  );
}
